/** @jsx jsx */
import { jsx, css } from '@emotion/react';
import { colors } from '@smartsheet/lodestar-core';
import {
  ArrowRightIcon,
  Icon,
  LoadingWaitingIcon,
} from '@smartsheet/lodestar-icons';
import { isNil } from 'ramda';
import { useState } from 'react';
import { Collapse } from 'react-collapse';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { UIWorkflowRunState } from '../../../../../../services/ducks/workflowDuck/workflowDuck';
import {
  getRunStateCategory,
  getRunStateString,
  hasError,
  hasOutput,
  stepSelector,
} from '../../../../../../services/ducks/workflowRunDuck/workflowRunDuck';
import { Column } from '../../../../../common/Column';
import { IconButtonNoSpacing } from '../../../../../common/IconButton';
import { CompletedIcon } from '../../../../../icons/CompletedIcon';
import { InProgressIcon } from '../../../../../icons/InProgressIcon';
import { StateIcon } from '../../../../../icons/StateIcon';
import { StopIcon } from '../../../../../icons/StopIcon';
import { TriggerIconDark } from '../../../../../icons/TriggerIconDark';
import { Print } from '../PrintJSON';

const getTypeIcon = (type: string) => {
  switch (type) {
    case 'TRIGGER':
      return TriggerIconDark;
    // case 'JUNCTION':
    //   return JunctionIcon;
    // case 'MODULE':
    //   return ModuleIcon;
    // case 'CHILD':
    //   return ChildWorkflowIcon;
    // case 'STATE':
    // case 'ANSWER':
    default:
      return StateIcon;
  }
};

const containerStyle = css`
  display: flex;
  align-items: center;
  height: 28px;
`;

const nameStyle = css`
  margin-left: 5px;
  font-weight: 400;
  font-size: 13px;
  line-height: 18px;
  span: first-child {
    font-weight: 600;
  }
`;

const errorMessageStyle = css`
  ${nameStyle};
  color: ${colors.danger};
`;

const nonObjectContainer = css`
  margin-left: 63px;
`;

function getIcon(state: UIWorkflowRunState) {
  const cat = getRunStateCategory(state);
  switch (cat) {
    // NOTE: Why is default empty? Should we just put the queued icon, the one used for all step?
    default:
    case UIWorkflowRunState.IN_PROGRESS:
      return <InProgressIcon />;
    case UIWorkflowRunState.WAITING:
      return <LoadingWaitingIcon />;
    case UIWorkflowRunState.STOPPED:
      return <StopIcon />;
    case UIWorkflowRunState.DONE:
      return <CompletedIcon />;
  }
}

export const StepsHeader = ({ stepId }: { stepId: string }) => {
  const { t: translate } = useTranslation();
  const step = useSelector(stepSelector(stepId));
  const [isOpen, setIsOpen] = useState(false);

  let child;
  if (hasError(step) && !isNil(step.error)) {
    child = (
      <Column css={nonObjectContainer}>
        <StopIcon />
        <span css={errorMessageStyle}>{step.error}</span>
      </Column>
    );
  } else if (hasOutput(step) && step.output) {
    child = (
      <Collapse isOpened={isOpen}>
        <Print
          obj={step.output}
          path={`${step.type === 'TRIGGER' ? '' : 'states.'}${step.id}`}
        />
      </Collapse>
    );
  } else if (step.state !== UIWorkflowRunState.DONE) {
    const statusMsg = getRunStateString(
      step.type === 'MODULE' ? getRunStateCategory(step.state) : step.state
    );
    child = (
      <Column css={nonObjectContainer}>
        {getIcon(step.state)}
        <span css={nameStyle}>{statusMsg}</span>
      </Column>
    );
  }

  const toggleHeader = () => setIsOpen(!isOpen);

  const names = step.name.split(':');
  let nameEl = (
    <div css={nameStyle}>
      <span>{step.name}</span>
    </div>
  );
  if (names.length > 1) {
    nameEl = (
      <div css={nameStyle}>
        <span>{names[0]}:</span>
        <span>{names[1]}</span>
      </div>
    );
  }

  const caretStyle = css`
    margin-right: 6px;
    transition: transform 200ms;
    ${isOpen && hasOutput(step) && step.output
      ? 'transform: rotate(90deg)'
      : null}
  `;

  return (
    <div>
      <div css={containerStyle}>
        {hasOutput(step) && step.output ? (
          <IconButtonNoSpacing
            onClick={toggleHeader}
            appearance="borderless"
            aria-label={
              isOpen
                ? translate('designer:runLog.collapse')
                : translate('designer:runLog.expand')
            }
          >
            <ArrowRightIcon css={caretStyle} />
          </IconButtonNoSpacing>
        ) : (
          <div
            css={css`
              width: 30px;
            `}
          ></div>
        )}
        <Icon
          svgComponent={getTypeIcon(step.type)}
          size={'small'}
          color={colors.neutralDark30}
        />
        {nameEl}
      </div>
      {child}
    </div>
  );
};
